import {
    add_unique_to_array,
    copy_object,
    is_nested_object,
    merge_deep,
    pending_object,
    present,
    remove_from_array,
    unique_id,
} from '@bitstillery/common/lib/utils'
import {
    deserialize_filter_param,
    generate_filters,
    is_filter_activated,
    reset_filters_sort,
} from '@bitstillery/common/lib/filters'
import {api, events, logger} from '@bitstillery/common/app'
import {unchecked} from '@bitstillery/common/lib/unchecked'
import EventEmitter from 'eventemitter3'
import m, {state, type State, type StateArray} from '@bitstillery/mithril'

import type {Filter, FiltersDescription} from '@bitstillery/common/types'
import type {Validator} from '@bitstillery/common/lib/validation'
import {FilterType, SortOrder} from '@bitstillery/common/api/fact2server'
import type {CollectionViewMeta, Option, OptionWithChildren} from '@bitstillery/common/api/fact2server'

const PAGE_SIZE = 25

/** Filters whose selection is passed as meta endpoint query params (filter_name -> param_name). Serialize type for URL fallback. */
const META_PARAMS_FROM_FILTERS: Record<string, {param_name: string; serialize: string}> = {
    include_snoozed: {param_name: 'include_snoozed', serialize: 'boolean'},
}

function get_meta_param_from_url(param_name: string, serialize: string): string | number | boolean | undefined {
    if (typeof window === 'undefined' || !window.location.search) return undefined
    const url_params: Partial<Record<string, string>> = Object.fromEntries(
        new URLSearchParams(window.location.search.substring(1)),
    )
    const value = url_params[param_name]
    return value !== undefined ? deserialize_filter_param(serialize, decodeURIComponent(value)) : undefined
}

function build_meta_params_from_filters(filters: Partial<Record<string, Filter>>): QueryParams {
    const params: QueryParams = {}
    for (const [filter_name, {param_name, serialize}] of Object.entries(META_PARAMS_FROM_FILTERS)) {
        const filter = filters[filter_name]
        const from_filter = filter?.selection ?? filter?.default
        const from_url = get_meta_param_from_url(param_name, serialize)
        params[param_name] = from_filter ?? from_url ?? false
    }
    return params
}

/** A value a collection puts on its query: the url's query string for a GET, the JSON body otherwise. */
export type QueryValue =
    | string
    | number
    | boolean
    | null
    | undefined
    | readonly QueryValue[]
    | {readonly [key: string]: QueryValue}

/** The parameters a collection query or meta request is built from. */
export type QueryParams = Record<string, QueryValue>

export type WorkflowInfo = {is_active: boolean; action_labels: string[]}

/**
 * A collection's filters by name. A collection that reads its filters by name declares them as the
 * `Filters` parameter of its `CollectionProxy` and `build_transforms`: `typeof filters` for the
 * `generate_filters` result it is initialized with, or a `type` (an interface lacks the index
 * signature) of the filters its meta endpoint describes.
 */
export type CollectionFilters = Record<string, Filter>

/** What the collection itself reads from a row: selection, detail and cache all key on the artkey. */
export interface CollectionRow {
    artkey: number | string
}

/**
 * The functions that take a row are declared as methods throughout this module: their parameters are
 * then checked bivariantly, which keeps a `CollectionProxy<SpecificRow>` (and its columns, bulk and
 * transforms) assignable where any collection is accepted.
 */
export type CollectionColumnSetup<T> = Array<{
    className?: string
    /** The column id is used to identify the column in bulk mode. */
    id?: string
    name?: string | (() => string)
    /** `context` is the CollectionItems' `context` attr, whatever the page passed there. */
    render(row: T, context?: unknown): m.Vnode | null | string | number | boolean | m.Vnode[] | undefined
    /** The cell's type, for its styling. */
    type?(row: T): string
    width?: string
}>

export interface CollectionSort {
    by: string
    defaults: {
        by: string
        order: 'asc' | 'desc'
    }
    options: [string, string][] | StateArray<[string, string]>
    order: 'asc' | 'desc'
}

/** A row as bulk editing tracks it: the collection's own row plus its edit state. */
export interface BulkRow<Row = CollectionRow> {
    changed: boolean
    invalid: boolean
    item: Row
    loading: boolean
    persist: boolean
    /** Each field's original value, to restore on cancel: restore[field_name] = item[field_name] */
    restore: Record<string, unknown>
}

/** A bulk row's fields: the validation of each editable field, or null for a field without one. */
export type BulkFields = Record<string, Validator | null>

/** Renders a bulk column's cell; `$v` is the row's `fields()`. */
interface BulkColumn<Row> {
    render(row: BulkRow<Row>, $v: BulkFields): m.Vnode | null | string | number | m.Vnode[] | undefined
}

export interface BulkSetup<Row = CollectionRow> {
    /** Actions in the header of the collection, over the rows that changed */
    actions(changed_rows: BulkRow<Row>[]): m.Vnode[]
    /** Additional bulk actions can be placed at the same location as collection row actions (right side)*/
    bulk_row_actions?(row: Row): m.Vnode[]
    icon?: string
    /** Main Bulk actions at the left side of the row of the collection */
    row_actions?(row: BulkRow<Row>, context?: unknown, workflow_info?: WorkflowInfo): m.Vnode[]

    columns: Record<string, BulkColumn<Row>['render']>
    entity_type: string
    fields(row: BulkRow<Row>): BulkFields
    persist?(this: void, rows: BulkRow<Row>[], mode: string): Promise<unknown>
    validation?(item: Row): Record<string, unknown>
}

export interface CollectionBulk<Row = CollectionRow> extends BulkSetup<Row> {
    icon?: string
    /** Keep track of row-level fields */
    _fields: Partial<Record<string, BulkFields>>
    /** The setup's own persist, which `persist` wraps; a setup may leave it out. */
    _persist?(this: void, rows: BulkRow<Row>[], mode: string): Promise<unknown>
    persist(this: void, rows: BulkRow<Row>[], action: string, alt_mode?: boolean): Promise<void>
    state: {
        entity_type: string
        /** The edit state of each row, by artkey; the row's model value is the collection's own item */
        rows: Record<string, BulkRow<Row>>
    }
}

export interface CollectionSetup<Row = CollectionRow> {
    /** Describes the bulk mode setup to use */
    bulk?: BulkSetup<Row>
    endpoint: {
        /** Use the endpoint's meta description to setup filters, transforms & sorting with */
        meta?: boolean
        /**
         * Context the meta endpoint needs to describe the right filters — the entity this collection
         * is being shown inside, typically. One list serving several editors cannot offer one set of
         * filters: a fragment's tab has no use for a filter over pages. Undefined clears the params an
         * earlier setup merged into this (module-singleton) collection; leaving it out keeps them.
         */
        meta_params?: QueryParams | undefined
        /** See the common api for supported methods. */
        method: 'get' | 'post' | 'put' | 'delete'
        /**
         * Path to the actual endpoint; FactServer uses RPC-style, Fact2Server REST-style.
         * If not provided, an endpoint transform is expected to fill the path dynamically.
         */
        path: string
        v2?: boolean
    }
    /**
     * Whether to restore filter & sortpreferences when initializing the collection.
     * Filters are restored when no filters are active at the moment of
     * collection initialization; either through meta=true or filters in the URL.
     * Default is true.
     * */
    lookup?: {
        enabled: boolean
        /** Filters that must NOT be remembered */
        ignore_filters: string[]
        /** The path to use for storing filter preferences; if not provided, the endpoint path is used. */
        path?: () => Promise<string>
    }
    /** Override the default (25) page size of the collection */
    page_size?: number
    selection?: {
        all: boolean
        field?: string
    }
    /** The sorting options to pass to the API endpoint; ignore this when using meta */
    sort?: Partial<CollectionSort>
    /** Syncs filter state from/to the route's query params; see filters_sort_to_url & url_to_filters_sort */
    url_sync?: boolean
    /** Enable cache with automatic cleanup of expired entries */
    cache?: boolean
    /**
     * Opt-in: when the endpoint answers 404 because its data is generated asynchronously and isn't ready yet
     * (e.g. the portal shop while the relation's price list is still being built), keep the collection in a
     * `pending` content_state instead of falling back to the empty "no results" view. `CollectionItems` then
     * shows a waiting widget and polls until the endpoint succeeds. Most 404s are permanent, so this is off
     * by default.
     */
    poll_when_not_ready?: boolean
}

/** Detail state for the expanded row: artkey identifies the row (null when none), key is the refresh key used for the row (e.g. for remounting the detail panel). */
export interface CollectionDetail {
    /** The artkey of the row to expand; a string for prefixed keys ('SOI-123') */
    artkey: number | string | null
    /** The key of the row to expand; update this key to redraw the whole detail panel */
    key: string
}

/** The query `filters_to_query` builds from the active filters; a type alias so it fits `QueryParams`. */
type CollectionFiltersQuery = {
    filters: Record<string, QueryValue>
    page_size: number
    search_terms: string
    sort_ascending: string
    sort_by: string
}

/** Shape of collection data passed to `state()`; the instance type is `State<CollectionState>`. */
export interface CollectionState<Row = CollectionRow> extends CollectionSetup<Row> {
    /** Starts at the default; init() applies the setup's own. */
    page_size: number
    bulk_mode: boolean
    content_state: 'outdated' | 'not_available' | 'normal' | 'available' | 'pending'
    detail: CollectionDetail
    /** Determines whether to query for more when scrolling to the end of the results */
    has_items: boolean
    inline_edit_mode: boolean
    items: Row[]
    loading: boolean
    lookup: {
        enabled: boolean
        ignore_filters: string[]
        path?: () => Promise<string>
    }
    /** Items marked for deletion (will be removed when navigating away) */
    marked: (number | string | null)[]
    query: {
        filters?: Record<string, QueryValue>
        /** An API V1 pagination property, used when the endpoint v2 is false */
        limit?: number
        /** Retrieve items from `offset` in a paginated collection */
        offset: number
        /** An API V2 pagination property, used when the endpoint v2 is true */
        page_size?: number
        /** Additional properties added by filters_to_query transform */
        [key: string]: unknown
    }
    /** Needed when the endpoint is not known yet and async initialization is needed. */
    ready: boolean
    /** When true, CollectionItems will scroll the selected row to the top of the visible area (e.g. after PanelContext select_next/select_previous) */
    scroll_to_selection: boolean
    /** Keeps track of selected rows */
    selection: {
        action?: {
            disabled?: () => unknown
            onclick?: () => unknown
            icon: string
        }
        /** Toggles inclusive or exclusive mode for ids */
        all: boolean
        /** A reference list of selected artkeys from the backend; useful for counters */
        current: number[]
        /** The ids to include or exclude, depending on the value of `all` (strings e.g. BULK or artkey ids) */
        ids: (number | string)[]
        locked: (number | string)[]
        mode: '' | 'select' | 'select_visible' | 'deselect'
        field?: string
    }
    sort: CollectionSort
    /** null once a response carried no total. */
    total: number | null
    view: {
        /** Global-index based cache of items */
        cache: boolean
        /** Expired items in the active viewport will be refreshed after this time. */
        cache_ttl_ms: number
        columns: number
        item_height_grid: number
        item_height_list: number
        /**
         * How many extra rows to FETCH above/below the rendered window (rendered window = viewport +
         * `overscan_rows`). Rendering an unloaded index yields a placeholder card, so data has to run
         * ahead of the render window or a flick lands on blanks while the request is in flight.
         */
        load_ahead_rows: number
        mode: 'list' | 'grid'
        /** How many extra rows to render above/below the viewport when virtualizing (default 2) */
        overscan_rows: number
        page_base_offset: number
        row_gap: number
        /** Current viewport range for virtualized rendering (1-based indices) */
        viewport_range: {start: number; end: number}
    }
}

/** TResult is the result of the api call. For new v2 calls this is the type of items[]. For example:

API call GetItems return a GetItemsCollectionViewResponse:
```
    {
        items: ItemResponse[],
        total: number,
    }
```
instantiate this template with ItemResult[].
*/
export interface CollectionTransforms<
    TResult = unknown,
    TCollectionRow = CollectionRow,
    TFilterMeta = unknown,
    TFilters extends CollectionFilters = CollectionFilters,
> {
    [x: string]: unknown

    /** When defined, it is used to apply filter metadata once the actual data has returned. */
    filter_metadata?:
        | {
              /** A filter statistics endpoint to query to. */
              endpoint?: string
              /**
               * The transform function to adapt filter state with, once the endpoint meta
               * endpoint returns. Can also be applied without endpoint, as a generic hook
               * to fill filter options with; it then gets the collection's filters.
               */
              transform(result: TFilterMeta): void | Promise<void>
          }
        | undefined
    /** Used to adapt filter values to query data with, until both ends are harmonized. */
    filters_to_query(filters: TFilters): QueryParams
    /** Called just after the items returned from the endpoint; used to adapt non-standard endpoint results. */
    items_queried(api_result: {
        result: TResult
        status_code: number
        total: number | null
    }): TCollectionRow[] | Promise<TCollectionRow[]>
    /** Takes care of filling the current selection state */
    selection(): Promise<void>
    collection_updated(): Promise<void>
}

export function build_transforms<
    TResult = unknown,
    TCollectionRow = CollectionRow,
    TFilterMeta = unknown,
    TFilters extends CollectionFilters = CollectionFilters,
>(options?: {
    with_filters_to_query?: (filters: TFilters) => QueryParams
    with_items_queried?: (api_result: {
        result: TResult
        status_code: number
        total: number | null
    }) => TCollectionRow[] | Promise<TCollectionRow[] | []>
    with_filter_metadata?: {endpoint?: string; transform: (result: TFilterMeta) => void | Promise<void>}
    with_selection?: () => Promise<void>
    with_collection_updated?: () => Promise<void>
}): CollectionTransforms<TResult, TCollectionRow, TFilterMeta, TFilters> {
    // Filter state isn't a query value; a collection's filters reach its query through the transform its
    // meta endpoint installs, or its own `with_filters_to_query`.
    const filters_to_query = options?.with_filters_to_query ?? ((): QueryParams => ({}))
    const items_queried = options?.with_items_queried
        ? options.with_items_queried
        : // Without an items_queried, the endpoint's result is the rows the collection declared.
          (api_result: {result: unknown; status_code: number; total: number | null}) =>
              unchecked<TCollectionRow[]>(api_result.result)

    const filter_metadata = options?.with_filter_metadata
    const selection = options?.with_selection ? options.with_selection : async () => {}
    const collection_updated = options?.with_collection_updated ? options.with_collection_updated : async () => {}

    return {
        filters_to_query,
        items_queried,
        filter_metadata,
        selection,
        collection_updated,
    }
}

/**
 * True only while CollectionItems performs a scroll-driven window redraw (the global redraw fired to slide
 * the virtual window). Nothing a row's data depends on changes during a scroll — the fine-grained signal
 * system already re-renders a row via a targeted redraw whenever anything it reads actually changes — so
 * virtualized row components read this in `onbeforeupdate` to skip the redundant re-render while scrolling.
 * A plain object (not reactive state) so toggling it never itself schedules a redraw.
 */
export const scroll_render = {active: false}

/**
 * Read a CSS custom property value, falling back to a default if unavailable
 */
function get_css_item_height(css_var: string, fallback: number): number {
    if (typeof document === 'undefined') return fallback
    try {
        const styles = getComputedStyle(document.documentElement)
        const value = styles.getPropertyValue(css_var).trim()
        if (value) {
            const num = Number(value.replace('px', ''))
            return Number.isFinite(num) && num > 0 ? num : fallback
        }
    } catch {
        // CSS not available yet, use fallback
    }
    return fallback
}

/** What `_init_from_meta` sets the collection up with. */
interface CollectionMeta {
    entities: CollectionViewMeta['endpoints']
    filters: CollectionFilters
}

export class CollectionProxy<Row extends CollectionRow = CollectionRow, Filters extends CollectionFilters = CollectionFilters> {
    bulk: CollectionBulk<Row> | null = null
    /** The endpoints the meta description names, for a collection initialized from one. */
    entities: CollectionViewMeta['endpoints'] | undefined
    events = new EventEmitter()
    /** Filled in place by `init`, from the description it is given or from the meta endpoint. */
    filters: Filters = pending_object()
    selection?: {
        action: {
            onclick(): unknown
            onselectionchanged(row: Row, selected_ids: (string | number)[]): unknown
            disabled(): unknown
            link(): string
            icon: string
        }
    }
    _id = unique_id()

    _meta?: CollectionMeta
    /** Sequence of the latest `query()`; a response from an earlier one is stale and dropped. */
    _query_seq = 0
    /** Exposed helper for jumping to a specific global index in virtualized mode */
    jump_to_index?: (target_index: number) => void
    /** Cache for loaded items: key is global index as string (e.g., "0", "1", "2"), value is the item and when it was cached */
    cache = new Map<string, {item: Row; timestamp: number}>()

    state: State<CollectionState<Row>> = state({
        page_size: PAGE_SIZE,
        bulk_mode: false,
        content_state: 'available',
        detail: {artkey: null, key: ''},
        endpoint: {
            meta: false,
            method: 'get',
            path: '',
        },
        has_items: true,
        items: [],

        inline_edit_mode: false,
        loading: true,
        lookup: {
            enabled: true,
            ignore_filters: [],
        },
        /** Items marked for deletion (will be removed when navigating away) */
        marked: [],
        query: {
            offset: 0,
        },
        ready: false,
        scroll_to_selection: false,
        selection: {
            all: true,
            current: [],
            ids: [],
            locked: [],
            mode: '',
        },
        sort: {
            by: '',
            defaults: {
                by: '',
                order: 'desc',
            },
            options: [],
            order: 'asc',
        },
        total: 0,
        url_sync: true,
        view: {
            cache: true,
            cache_ttl_ms: 60000,
            columns: 4,
            item_height_grid: get_css_item_height('--grid-item-height', 400),
            item_height_list: get_css_item_height('--list-item-height', 150),
            load_ahead_rows: 6,
            mode: 'grid',
            overscan_rows: 2,
            page_base_offset: 0,
            row_gap: 0,
            viewport_range: {
                end: 0,
                start: 0,
            },
        },
    })

    transforms: CollectionTransforms<unknown, Row, unknown, Filters> = build_transforms<unknown, Row, unknown, Filters>()

    /**
     * The collection is initialized from a meta endpoint, which provides
     * sorting, filtering & endpoint information.
     * @param endpoint
     */
    async _init_from_meta(endpoint: CollectionState<Row>['endpoint']): Promise<CollectionMeta | undefined> {
        const meta_endpoint = `${endpoint.path}/meta`
        const meta_params = {...build_meta_params_from_filters(this.filters), ...(endpoint.meta_params ?? {})}
        logger.info(`[collection] loading meta data: ${meta_endpoint}`)

        // The path is configured at runtime, so no route types it; every collection's meta answers this.
        const meta_response = await api.get<CollectionViewMeta>(meta_endpoint, meta_params)
        if (!meta_response.success) {
            return
        }
        const {result} = meta_response

        // Setup sorting
        merge_deep(this.state.sort, {
            defaults: {
                by: result.sort_definition.default_field.id,
                order: result.sort_definition.default_field.order === SortOrder.DESC ? 'desc' : 'asc',
            },
            by: result.sort_definition.default_field.id,
            order: result.sort_definition.default_field.order === SortOrder.DESC ? 'desc' : 'asc',
            options: present(result.sort_definition.fields, 'meta sort fields').map((i) => [i.id, i.label]),
        })

        const filter_descriptions: Record<string, Record<string, unknown>> = {}
        let order = 0

        // fact2server sets each definition's filter_type to the type of its class (collection_view_meta.py);
        // the field checks below pick that class out of the union.
        for (const _filter of result.filter_definitions) {
            // The frontend holds a range's default as a [min, max] tuple.
            let range_default: (number | null | undefined)[] | null = null
            if (_filter.filter_type === FilterType.SELECT_SINGLE && 'options' in _filter) {
                const definition = _filter
                const description: Record<string, unknown> = {
                    options: definition.options.map((i: Option) => [i.value, i.label]),
                    selection: copy_object(definition.default),
                    serialize: definition.serialize,
                    widget: definition.widget,
                }
                if ('context' in definition) {
                    description.context = definition.context
                }
                filter_descriptions[definition.name] = description
            } else if (_filter.filter_type === FilterType.SELECT_MULTIPLE && 'options' in _filter) {
                const definition = _filter
                const option_list: (Option | OptionWithChildren)[] = definition.options
                let options
                if (option_list.some((i) => 'children' in i)) {
                    options = option_list.map((i) => {
                        const children = 'children' in i ? i.children : []
                        return [i.value, i.label, 0, children.length ? children.map((j) => [j.value, j.label, 0, []]) : []]
                    })
                } else {
                    options = option_list.map((i) => [i.value, i.label])
                }

                filter_descriptions[definition.name] = {
                    icon: 'filterMultiple',
                    options,
                    serialize: [definition.serialize],
                }
            } else if (_filter.filter_type === FilterType.SELECT_RANGE && 'scale' in _filter) {
                const definition = _filter
                if (definition.default.max_value === null) {
                    definition.default.max_value = Infinity
                }
                range_default = [definition.default.min_value, definition.default.max_value]

                filter_descriptions[definition.name] = {
                    icon: 'range',
                    infinity: definition.infinity,
                    scale: [definition.scale.min_value, definition.scale.max_value],
                    serialize: [definition.serialize],
                    unit: definition.unit,
                }
            } else if (_filter.filter_type === FilterType.TEXT) {
                const definition = _filter
                filter_descriptions[definition.name] = {
                    icon: 'search',
                    input: '',
                    serialize: definition.serialize,
                }
            } else if (_filter.filter_type === FilterType.TOGGLE) {
                const definition = _filter
                filter_descriptions[definition.name] = {
                    icon: 'toggle-switch-outline',
                    serialize: definition.serialize,
                }
            }

            merge_deep(filter_descriptions[_filter.name], {
                default: range_default ?? _filter.default,
                help: _filter.help,
                placement: {order},
                selection: range_default ? [...range_default] : copy_object(_filter.default),
                type: _filter.filter_type,
            })

            order += 1
        }

        // Each branch above describes the filter state of the type it handles.
        const filters: CollectionFilters = generate_filters<FiltersDescription>(filter_descriptions)

        // Setup basic transform; the filters can directly be used
        // in query parameters, because they match the backend's API.
        this.transforms.filters_to_query = (filters) => {
            const query: CollectionFiltersQuery = {
                filters: {},
                page_size: this.state.page_size,
                search_terms: '',
                sort_by: this.state.sort.by,
                sort_ascending: this.state.sort.order === 'asc' ? 'ASC' : 'DESC',
            }

            for (const [key, filter] of Object.entries(filters)) {
                if (is_filter_activated(filter)) {
                    if (key === 'search') {
                        // The meta's `search` is a TEXT filter, whose selection is the typed text.
                        if (typeof filter.selection === 'string') {
                            query.search_terms = filter.selection.split(' ').join(',')
                        }
                    } else {
                        if (filter.type === 'SELECT_RANGE') {
                            const range: number[] = filter.selection
                            query.filters[key] = {
                                min_value: range[0],
                                max_value: range[1],
                            }
                        } else {
                            query.filters[key] = filter.selection
                        }
                    }
                }
            }

            return query
        }

        const entities = result.endpoints
        return {entities, filters}
    }

    /**
     * Refetch one or more rows by artkey and merge them into the current page, or requery when a row is not in `state.items`.
     * (!) Only V2 collection endpoints are expected to be able to filter on artkeys.
     */
    async _refresh_items_by_artkeys(item_artkeys: (string | number | null | undefined)[]) {
        // A nullish artkey would serialize as the literal 'null', which the API rejects as an artkey.
        const artkeys = item_artkeys.filter((ak): ak is string | number => ak !== null && ak !== undefined)
        if (artkeys.length === 0) {
            return
        }
        if (!this.state.endpoint.v2) {
            await this.reset_query()
            return
        }

        const response = await api.request(
            this.state.endpoint.method,
            this.state.endpoint.path,
            {artkeys, sort_by: 'product_name'},
            true,
        )
        // The collection's own endpoint answers its rows.
        const updated_items = response.success && Array.isArray(response.result) ? unchecked<Row[]>(response.result) : []

        if (!updated_items.length) {
            logger.warn(`[collection] _refresh_items_by_artkeys: no items found for artkeys ${artkeys.join(', ')}`)
            return
        }

        const by_artkey = new Map(updated_items.map((row: Row) => [row.artkey, row]))

        const any_missing_locally = artkeys.some((ak) => this.state.items.findIndex((i) => i.artkey === ak) < 0)

        if (any_missing_locally) {
            await this.reset_query()
            this.state.detail.artkey = null
            this.state.detail.key = unique_id()
            return
        }

        for (const item_artkey of artkeys) {
            const fetched = by_artkey.get(item_artkey)
            if (!fetched) {
                logger.warn(`[collection] _refresh_items_by_artkeys: no item in response for artkey ${item_artkey}`)
                continue
            }
            const item_index = this.state.items.findIndex((i) => i.artkey === item_artkey)
            if (item_index >= 0) {
                merge_deep(this.state.items[item_index], fetched)
                const items = await this.transforms.items_queried({
                    result: [this.state.items[item_index]],
                    status_code: 200,
                    total: null,
                })
                merge_deep(this.state.items[item_index], items[0])
            }
        }
        this.state.detail.key = unique_id()
    }

    async fetch_items(offset: number, limit: number): Promise<{items: Row[]; total: number | null; status_code: number}> {
        const query_params = {
            ...this.state.query,
            ...this.transforms.filters_to_query(this.filters),
            offset,
            limit,
        }

        const {result, status_code, total} = await api.request(
            this.state.endpoint.method,
            this.state.endpoint.path,
            query_params,
            this.state.endpoint.v2,
        )

        const items = await this.transforms.items_queried({result, status_code, total: total ?? null})
        return {items, total: total ?? null, status_code}
    }

    async init(
        setup: CollectionSetup<Row>,
        filters?: FiltersDescription,
        transforms?: CollectionTransforms<unknown, Row, unknown, Filters>,
    ) {
        // A CollectionProxy is a module singleton that outlives the page mounting it, and `url_sync` is
        // switched off mid-life (by the popstate handler in CollectionItems) to suppress one URL
        // write-back. Re-arm it here so a fresh mount always reads its filters from the URL again —
        // otherwise a collection left switched off silently ignores the query it was navigated to with.
        // Before the merge, so a setup that opts out permanently (`url_sync: false`) still wins.
        this.state.url_sync = true
        merge_deep(this.state, setup)
        this.state.page_size = setup.page_size || PAGE_SIZE

        this.transforms = transforms || this.transforms

        // Determine the type of endpoint (Fact2Server/Factserver) from the endpoint description.
        this.state.endpoint.v2 = this.state.endpoint.method !== 'post' || this.state.endpoint.path.includes('/')

        logger.debug(`[collection] init ${setup.endpoint.path}`)

        const sort_defaults = this.state.sort.defaults
        sort_defaults.by = this.state.sort.by
        sort_defaults.order = this.state.sort.order

        if (setup.bulk) {
            logger.debug('[collection] setup bulk mode')
            const bulk_setup = setup.bulk
            // Filled in on the setup object itself, which is what the caller keeps hold of.
            this.bulk = Object.assign(bulk_setup, {
                _fields: {},
                ...(bulk_setup.persist === undefined ? {} : {_persist: bulk_setup.persist}),
                // Wrapper that performs common plumbing before calling the bulk.persist.
                persist: async (changed_rows: BulkRow<Row>[], action: string) => {
                    await present(present(this.bulk, 'collection bulk')._persist, 'bulk persist')(changed_rows, action)
                    // Reset the restore values for the items.
                    for (const row of changed_rows) {
                        const item = row.item
                        if (!is_nested_object(item)) {
                            continue
                        }
                        for (const field_name of Object.keys(row.restore)) {
                            row.restore[field_name] = item[field_name]
                        }
                    }
                },
                state: state({
                    active: false,
                    entity_type: setup.bulk.entity_type,
                    rows: {},
                }),
            })
        }

        if (setup.endpoint.meta) {
            const meta = present(await this._init_from_meta(this.state.endpoint), 'collection meta')
            this._meta = copy_object(meta)
            Object.assign(this.filters, meta.filters)

            this.entities = meta.entities
        } else {
            Object.assign(this.filters, filters)
        }

        this.state.ready = true
        events.emit(`collection:ready:${this._id}`)
    }

    async is_ready() {
        if (this.state.ready) {
            return true
        }

        return new Promise<boolean>((resolve) => {
            events.on(`collection:ready:${this._id}`, () => {
                resolve(true)
            })
        })
    }

    item_deselect(item: Row) {
        const ids = this.state.selection.ids
        if (this.state.selection.mode === 'select_visible') {
            remove_from_array(ids, item.artkey)
        } else if (this.state.selection.all) {
            add_unique_to_array(ids, item.artkey)
        } else {
            remove_from_array(ids, item.artkey)
        }

        this.selection?.action.onselectionchanged(item, this.state.selection.ids)
    }

    item_select(item: Row) {
        const ids = this.state.selection.ids
        if (this.state.selection.mode === 'select_visible') {
            add_unique_to_array(ids, item.artkey)
        } else if (this.state.selection.all) {
            remove_from_array(ids, item.artkey)
        } else {
            add_unique_to_array(ids, item.artkey)
        }

        this.selection?.action.onselectionchanged(item, this.state.selection.ids)
    }

    async query() {
        // Until `init` returns, `filters_to_query` is still the default that `build_transforms` ships
        // with: the one the meta response installs is what puts `sort_by`, `page_size` and the filters
        // on the query. Firing now sends a request the server refuses for a missing `sort_by`. Nothing is
        // lost by skipping it — whatever the caller put on `state.query` rides along with the load that
        // CollectionItems performs the moment the collection turns ready.
        if (!this.state.ready) {
            logger.debug('[collection] query before ready; the initial load will carry it')
            return
        }
        this.state.loading = true
        Object.assign(this.state.query, this.transforms.filters_to_query(this.filters))
        // Overlapping queries (typing in a search filter) resolve out of order. Without the sequence
        // check an older response lands on top of a newer one, and `state.query.offset` read after the
        // await may have been bumped by a load-more meanwhile, turning a replace into an append that
        // lists the overlapping rows twice.
        const query_seq = ++this._query_seq
        const offset = this.state.query.offset

        const {result, status_code, total} = await api.request(
            this.state.endpoint.method,
            this.state.endpoint.path,
            this.state.query,
            this.state.endpoint.v2,
        )

        // Wait for optional additional data to kick in, before trying to render items.
        let items = await this.transforms.items_queried({result, status_code, total: total ?? null})
        if (query_seq !== this._query_seq) {
            // `loading` stays up; the newer query clears it.
            return {result, status_code, total}
        }
        await this.reconcile_content(status_code, async () => this.query())
        this.state.total = total ?? null

        // In case a collection returns no response, we don't want to throw an error.
        if (!Array.isArray(items)) {
            items = []
        }

        if (offset > 0) {
            this.state.items.push.apply(this.state.items, items)
        } else {
            this.state.items.splice(0, this.state.items.length, ...items)
        }

        void this.transforms.collection_updated()
        this.events.emit('state_items_updated')

        if (!this.state.total) {
            // We don't have a total to determine whether to load more; when
            // the currently retrieved item amount fit with the requested size,
            // we assume there are more.
            this.state.has_items = items.length === this.state.page_size
        } else {
            this.state.has_items = this.state.items.length < this.state.total
        }

        if (offset === 0 && this.transforms.filter_metadata) {
            // No need to await the filter results.
            for (const filter of Object.values(this.filters)) {
                filter.loading = true
            }

            if (this.transforms.filter_metadata.endpoint) {
                const {result: meta_data} = await api.post(this.transforms.filter_metadata.endpoint, this.state.query, true)
                if (status_code === 404) return {result: meta_data, status_code, total: 0}
                await this.transforms.filter_metadata.transform(meta_data)
            } else {
                // Regular one-time filter setup also goes into the transform.
                await this.transforms.filter_metadata.transform(this.filters)
            }
        }

        for (const filter of Object.values(this.filters)) {
            if (filter.loading) {
                filter.loading = false
            }
        }

        this.state.loading = false
        return {result, status_code, total}
    }

    async query_virtualized(offset: number, limit: number) {
        this.state.loading = true

        const should_process_filter_metadata = offset === 0 && !!this.transforms.filter_metadata
        if (should_process_filter_metadata) {
            for (const filter of Object.values(this.filters)) {
                filter.loading = true
            }
        }

        const {items, status_code, total} = await this.fetch_items(offset, limit)
        await this.reconcile_content(status_code, async () => this.query_virtualized(offset, limit))
        if (should_process_filter_metadata) {
            const filter_metadata = present(this.transforms.filter_metadata, 'collection filter_metadata')
            if (filter_metadata.endpoint) {
                const {result: meta_data} = await api.post(
                    filter_metadata.endpoint,
                    {
                        ...this.state.query,
                        ...this.transforms.filters_to_query(this.filters),
                    },
                    true,
                )
                if (status_code !== 404) {
                    // Read again: a remount's init() may have swapped the transforms while the request was out.
                    await present(this.transforms.filter_metadata, 'collection filter_metadata').transform(meta_data)
                }
            } else {
                await filter_metadata.transform(this.filters)
            }
        }

        // Always clear the initial `loading: true` flag from generate_filters, even
        // for collections without a filter_metadata transform. Otherwise the filters
        // never signal "done loading" and the FiltersActive spinner spins forever.
        for (const filter of Object.values(this.filters)) {
            if (filter.loading) {
                filter.loading = false
            }
        }

        this.state.loading = false
        return {items, total, status_code}
    }

    async reconcile_content(status_code: number, query_function: () => Promise<unknown>) {
        // Handle PARTIAL_CONTENT(204) or NO_CONTENT (206)
        if (status_code === 204 || status_code === 206) {
            this.state.content_state = status_code === 204 ? 'outdated' : 'not_available'
            setTimeout(() => {
                void query_function()
            }, 2000)
        } else if (status_code === 422) {
            // 422; might be a change in filter definitions (illegal request). Reset filters and retry.
            await reset_filters_sort(this)
            setTimeout(() => {
                void this.query()
            }, 2000)
        } else if (status_code === 404 && this.state.poll_when_not_ready) {
            // The endpoint's data isn't generated yet (e.g. the relation's price list). Flag the collection
            // as pending so CollectionItems can show a waiting widget and poll — rather than the empty "no
            // results" view. The poll runs from CollectionItems (it owns item population in virtualized mode);
            // a retry scheduled here would fetch-and-discard.
            this.state.content_state = 'pending'
        } else if (status_code < 299) {
            this.state.content_state = 'normal'
        }
    }

    /**
     * Refreshes the meta endpoint data and updates filters/context counts.
     * This is useful when context counts need to be updated after an action.
     */
    async refresh_meta() {
        if (!this.state.endpoint.meta) {
            return
        }
        const meta = await this._init_from_meta(this.state.endpoint)
        // The failed request has notified; the filters keep the counts they have.
        if (!meta) {
            return
        }
        merge_deep(this._meta, copy_object(meta))
        // Update context counts in filters without changing selections
        for (const [filter_name, filter] of Object.entries(this.filters)) {
            const meta_filter = meta.filters[filter_name]
            // Only a workflow filter has a context; the meta describes this filter as the same type.
            if (meta_filter && 'context' in meta_filter && filter.type === 'SELECT_SINGLE') {
                filter.context = meta_filter.context
            }
        }
    }

    async reset_query() {
        logger.info('[collection] reset query')
        // Same reason `query` bails while the collection is initializing — and dropping the items a
        // list is still showing to replace them with a request that never runs is worse than doing
        // nothing at all.
        if (!this.state.endpoint.path || !this.state.ready) {
            return
        }
        this.state.query.offset = 0
        this.state.items.splice(0, this.state.items.length)
        await this.query()
    }

    reset_selection() {
        this.state.selection.mode = ''
        void this.transforms.selection()
    }

    select_previous() {
        if (this.state.selection.all) {
            // selection.ids is an exclude list here; switch it off first.
            this.state.selection.all = false
        }
        let new_index = -1
        if (!this.state.selection.ids.length && this.state.items.length) {
            // Select the last item.
            new_index = 0
        } else if (this.state.selection.ids.length === 1) {
            // Select the next item
            const current_index = this.state.items.findIndex((i) => i.artkey === this.state.selection.ids[0])
            if (current_index > 0) {
                new_index = current_index - 1
            } else if (current_index === 0) {
                new_index = this.state.items.length - 1
            }
        }

        const new_item = this.state.items[new_index]
        if (new_item) {
            this.state.selection.ids.splice(0, this.state.selection.ids.length, new_item.artkey)
        } else {
            this.state.selection.ids.splice(0, this.state.selection.ids.length)
        }
    }

    /**
     * Selects the next item in the collection.
     */
    select_next() {
        if (this.state.selection.all) {
            // selection.ids is an exclude list here; switch it off first.
            this.state.selection.all = false
        }

        let new_index = -1
        if (!this.state.selection.ids.length && this.state.items.length) {
            // Select the first item.
            new_index = 0
        } else if (this.state.selection.ids.length === 1) {
            // Select the next item
            const current_index = this.state.items.findIndex((i) => {
                return i.artkey === this.state.selection.ids[0]
            })
            if (current_index >= 0 && current_index < this.state.items.length - 1) {
                new_index = current_index + 1
            } else if (current_index === this.state.items.length - 1) {
                // Wrap around to the first item
                new_index = 0
            }
        }

        const new_item = this.state.items[new_index]
        if (new_item) {
            this.state.selection.ids.splice(0, this.state.selection.ids.length, new_item.artkey)
        } else {
            this.state.selection.ids.splice(0, this.state.selection.ids.length)
        }
    }

    select_one(item_artkey: number | string | null, detail = false) {
        if (detail) {
            if (item_artkey) {
                this.state.detail.artkey = Number(item_artkey)
            } else {
                this.state.detail.artkey = null
            }
        }
        this.state.selection.all = false
        logger.debug(`[collection] select item: ${String(item_artkey)}`)
        if (item_artkey !== null) {
            this.state.selection.ids.splice(0, this.state.selection.ids.length, item_artkey)
        } else {
            this.state.selection.ids.splice(0, this.state.selection.ids.length)
        }
    }

    select_none() {
        this.state.selection.all = false
        this.state.selection.ids.splice(0, this.state.selection.ids.length)
    }

    /** How many rows the selection covers; null for "all" when the endpoint sent no total to subtract from. */
    selection_count(): number | null {
        if (this.state.selection.all) {
            return this.state.total === null ? null : this.state.total - this.state.selection.ids.length
        } else {
            return this.state.selection.ids.length
        }
    }

    /**
     * Marks an item for deletion. The item will be removed when navigating away from it.
     * @param artkey - The item's id to mark for deletion.
     */
    mark(artkey: number | string | null) {
        if (!this.state.marked.includes(artkey)) {
            this.state.marked.push(artkey)
        }
    }

    /**
     * Unmarks an item for deletion.
     * @param artkey - The item's id to unmark.
     */
    unmark(artkey: number) {
        const index = this.state.marked.indexOf(artkey)
        if (index >= 0) {
            this.state.marked.splice(index, 1)
        }
    }

    /**
     * Checks if an item is marked for deletion.
     * @param artkey - The item's id to check.
     */
    is_marked(artkey: number | string): boolean {
        return this.state.marked.includes(artkey)
    }

    /**
     * Removes the item with `artkey` from the collection items state.
     * @param artkey - The item's id to look for.
     */
    soft_delete(artkey: number | string | null) {
        this.state.items.splice(
            this.state.items.findIndex((i) => i.artkey === artkey),
            1,
        )
        if (this.state.total) {
            this.state.total -= 1
        }
    }

    toggle_detail(item: Row) {
        if (this.state.detail.artkey === item.artkey) {
            this.state.detail.artkey = null
        } else {
            this.state.detail.artkey = item.artkey
        }
    }

    async update_item(item_artkey?: string | number | null) {
        logger.debug(`[collection] update_item: ${String(item_artkey)}`)
        await this._refresh_items_by_artkeys([item_artkey])
    }

    /**
     * Like {@link update_item}, but fetches all given artkeys in one request. Nullish entries are skipped; duplicates are de-duplicated.
     */
    async update_items(item_artkeys: (string | number | null | undefined)[]) {
        const seen = new Set<string | number>()
        const keys: (string | number)[] = []
        for (const k of item_artkeys) {
            if (k === null || k === undefined) {
                continue
            }
            if (seen.has(k)) {
                continue
            }
            seen.add(k)
            keys.push(k)
        }
        if (keys.length === 0) {
            return
        }
        logger.debug(`[collection] update_items: ${keys.join(', ')}`)
        await this._refresh_items_by_artkeys(keys)
    }

    /**
     * Check if an item at a given global_index is cached and not expired
     */
    is_item_cached(global_index: number) {
        if (!this.state.view.cache) {
            return false
        }
        const cache_key = String(global_index)
        const cached = this.cache.get(cache_key)
        if (!cached) {
            return false
        }
        return cached
    }

    is_item_cached_expired(global_index: number): boolean {
        if (!this.state.view.cache) {
            // If we don't have caching enabled, we can't have expired items.
            return false
        }
        const cached_item = this.is_item_cached(global_index)
        if (!cached_item) {
            return true
        }

        const cache_ttl_ms = this.state.view.cache_ttl_ms
        const age = Date.now() - cached_item.timestamp
        return age > cache_ttl_ms
    }

    /**
     * Get cached items for a given offset/limit range.
     * Returns an object with:
     * - items: array of items if all indices are cached and valid, null otherwise
     * - all_items: array of all cached items (including expired) for the range, may have nulls for missing indices
     * - expired_indices: array of global indices that are cached but expired
     * - has_missing: true if any indices in the range are not in cache
     */
    get_cached_items(
        offset: number,
        limit: number,
    ): {items: Row[] | null; all_items: (Row | null)[]; expired_indices: number[]; has_missing: boolean} {
        if (!this.state.view.cache) {
            return {items: null, all_items: [], expired_indices: [], has_missing: true}
        }
        const cache_ttl_ms = this.state.view.cache_ttl_ms
        const now = Date.now()
        const valid_items: Row[] = []
        const all_items: (Row | null)[] = []
        const expired_indices: number[] = []
        let has_missing = false

        // Check each index in the range
        for (let i = 0; i < limit; i++) {
            const global_index = offset + i
            const cache_key = String(global_index)
            const cached = this.cache.get(cache_key)

            if (!cached) {
                has_missing = true
                all_items.push(null)
                continue
            }

            all_items.push(cached.item)
            const age = now - cached.timestamp
            if (age > cache_ttl_ms) {
                // Expired but still in cache (will be refreshed, not removed)
                expired_indices.push(global_index)
            } else {
                // Valid cached item
                valid_items.push(cached.item)
            }
        }

        // If all indices are cached and valid, return the items array
        if (!has_missing && expired_indices.length === 0) {
            return {items: valid_items, all_items, expired_indices: [], has_missing: false}
        }

        // Otherwise return null items with status information
        return {items: null, all_items, expired_indices, has_missing}
    }

    /**
     * Refresh expired items by querying the API with their artkeys.
     * Updates cache entries in place with fresh data.
     * @param expired_indices Array of global indices that are expired
     * @param viewport_offset Offset of the visible viewport (for API query)
     * @param viewport_limit Limit of the visible viewport (for API query)
     * @returns Map of global_index -> refreshed item, or null if refresh failed
     */
    async refresh_expired_items(
        expired_indices: number[],
        viewport_offset?: number,
        viewport_limit?: number,
    ): Promise<Map<number, Row> | null> {
        if (!this.state.view.cache || expired_indices.length === 0) {
            return null
        }

        // Collect artkeys from expired cache entries
        const artkeys: (number | string)[] = []
        const index_to_artkey = new Map<number, number | string>()
        for (const global_index of expired_indices) {
            const cache_key = String(global_index)
            const cached = this.cache.get(cache_key)
            if (cached?.item?.artkey) {
                artkeys.push(cached.item.artkey)
                index_to_artkey.set(global_index, cached.item.artkey)
            }
        }

        if (artkeys.length === 0) {
            return null
        }

        try {
            // Query API with artkeys filter
            // offset and limit are required by the API
            // Set offset to 0 and limit to the number of artkeys to ensure we get all matching items
            const query_without_pagination: Record<string, unknown> = {}
            for (const [key, value] of Object.entries(this.state.query)) {
                if (key !== 'limit' && key !== 'page_size') {
                    query_without_pagination[key] = value
                }
            }
            const filters_query = this.transforms.filters_to_query(this.filters)
            // Remove limit/page_size from filters_query if present (we'll set limit explicitly)
            const filters_without_pagination: QueryParams = {}
            for (const [key, value] of Object.entries(filters_query)) {
                if (key !== 'limit' && key !== 'page_size') {
                    filters_without_pagination[key] = value
                }
            }

            // Use viewport offset/limit if provided, otherwise fallback to 0 and artkeys.length
            const query_offset = viewport_offset !== undefined ? viewport_offset : 0
            const query_limit = viewport_limit !== undefined ? viewport_limit : artkeys.length

            const query_params = {
                ...query_without_pagination,
                ...filters_without_pagination,
                offset: query_offset, // Use viewport offset to match visible range
                limit: query_limit, // Use viewport limit to match visible range
                artkeys,
                sort_by: 'artkey', // Sort by artkey to match order
            }

            const {result, status_code} = await api.request(
                this.state.endpoint.method,
                this.state.endpoint.path,
                query_params,
                this.state.endpoint.v2,
            )

            if (status_code >= 400 || !result) {
                logger.warn(`[collection] refresh_expired_items: API call failed with status ${status_code}`)
                return null
            }

            // Transform items if needed (handles different response formats)
            let items = await this.transforms.items_queried({result, status_code, total: null})

            // After transformation, items should be an array
            if (!Array.isArray(items)) {
                logger.warn(`[collection] refresh_expired_items: transformed result is not an array, got ${typeof items}`)
                return null
            }

            // Create artkey -> item mapping
            const artkey_to_item = new Map<number | string, Row>()
            for (const item of items) {
                if (item.artkey) {
                    artkey_to_item.set(item.artkey, item)
                }
            }

            // Update cache entries with fresh data
            const refreshed = new Map<number, Row>()
            const now = Date.now()
            let missing_artkeys: (number | string)[] = []
            for (const global_index of expired_indices) {
                const artkey = index_to_artkey.get(global_index)
                if (artkey) {
                    const fresh_item = artkey_to_item.get(artkey)
                    if (fresh_item) {
                        const cache_key = String(global_index)
                        this.cache.set(cache_key, {item: fresh_item, timestamp: now})
                        refreshed.set(global_index, fresh_item)
                    } else {
                        missing_artkeys.push(artkey)
                    }
                }
            }

            return refreshed
        } catch (error) {
            logger.error('[collection] refresh_expired_items: error refreshing items', error)
            return null
        }
    }

    /**
     * Store items in cache with current timestamp.
     * Each item is stored individually by its global index.
     */
    set_cached_items(offset: number, items: Row[]) {
        if (!this.state.view.cache) {
            return
        }

        const now = Date.now()

        // Store each item individually by its global index
        for (const [i, item] of items.entries()) {
            const global_index = offset + i
            const cache_key = String(global_index)
            this.cache.set(cache_key, {
                item, // Store individual item
                timestamp: now,
            })
        }
    }

    /**
     * Clear entire cache. Called when filters or sort change,
     * since items at the same index are now different items.
     * Expired items are refreshed in place, not removed via this method.
     */
    clear_cache() {
        if (!this.state.view.cache) {
            return
        }
        const prev_size = this.cache.size
        this.cache.clear()
        if (prev_size > 0) {
            logger.debug(`[collection] cache cleared: removed ${prev_size} entries`)
        }
    }
}

/**
 * Generates a CSS grid template column definition string based on
 * the provided columns and selection status.
 *
 * @param columns - An array of column objects, each containing a `width` property.
 * @param has_selection - A flag indicating whether a selection column should be included.
 * @param bulk_mode - A flag indicating whether the collection is in bulk mode.
 * @returns The resulting CSS grid template columns string.
 */
export function grid_columns(
    // The type is asked without a row: a column its type hides is hidden for every row.
    columns: {type?: string | ((...row: never[]) => string); width?: string}[],
    has_selection: boolean,
    bulk_mode: boolean,
) {
    let grid_template_cols = ''
    if (has_selection || bulk_mode) {
        grid_template_cols += '40px '
    } else {
        grid_template_cols += '0px '
    }

    grid_template_cols += columns
        .filter((i) => {
            if (i.type) {
                let _type = i.type
                if (typeof i.type === 'function') {
                    _type = i.type()
                }
                if (_type === 'hidden') {
                    return false
                }
            }
            return true
        })
        .map((i) => {
            if (i.width) {
                return i.width
            } else {
                return '1fr'
            }
        })
        .join(' ')
    return grid_template_cols
}
